import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { EditableTaskStatus, Task } from "@recobra/schemas/task";
import { Badge } from "@recobra/ui/badge";
import { Button } from "@recobra/ui/button";
import { DialogHeader, DialogTitle } from "@recobra/ui/dialog";
import { Separator } from "@recobra/ui/separator";

import { caseSummaryQueryOptions } from "../../cases";
import { useTaskName } from "../task-data";
import { taskContextIdentifier } from "../task-display";
import { taskStatusLabel } from "../task-display";
import { TaskStatusIcon, TaskStatusSelect } from "../task-display";
import { TaskDeadlineBadge } from "../task-display";

export const TaskModalHeader: FC<{
  task: Task;
  isUpdating: boolean;
  onViewContext?: () => void;
  onStatusChange?: (status: EditableTaskStatus, waitingStatus?: string) => void;
}> = ({ task, isUpdating, onViewContext, onStatusChange }) => {
  const taskName = useTaskName();
  const queryClient = useQueryClient();
  const { data: caseSummary } = useQuery({
    ...caseSummaryQueryOptions(queryClient, task.contextId),
    enabled: task.contextType === "case",
  });
  const caseTypeLabel =
    task.contextType === "case" && caseSummary
      ? m.case_type_label({ caseType: caseSummary.caseType })
      : undefined;
  const context = (
    <>
      {m.task_context_type_label({ contextType: task.contextType })}{" "}
      <span className="font-mono">{taskContextIdentifier(task)}</span>
    </>
  );

  return (
    <>
      <DialogHeader>
        <div className="flex min-w-0 items-start justify-between gap-2 pr-8">
          <div className="flex min-w-0 flex-col gap-2">
            <DialogTitle>{taskName(task.type)}</DialogTitle>
            <div className="flex min-w-0 items-center gap-2">
              {onViewContext ? (
                <Button variant="link" size="xs" onClick={onViewContext}>
                  {context}
                  <ArrowUpRight />
                </Button>
              ) : (
                <span className="truncate text-xs text-muted-foreground">{context}</span>
              )}
              {caseTypeLabel ? (
                <Badge variant="secondary" title={caseTypeLabel}>
                  {caseTypeLabel}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {onStatusChange ? (
              <TaskStatusSelect task={task} disabled={isUpdating} onChange={onStatusChange} />
            ) : (
              <span className="flex items-center gap-1.5 text-sm">
                <TaskStatusIcon status={task.status} />
                {taskStatusLabel(task.status, task.waitingStatus)}
              </span>
            )}
            <TaskDeadlineBadge task={task} />
          </div>
        </div>
      </DialogHeader>
      <Separator />
    </>
  );
};
